import { Observable, of } from 'rxjs';
import { retry, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_RETRY_COUNT, API_RETRY_DELAY, CACHE_TIMEOUT } from '@shared/constants';
import { withAPICache } from '@shared/interceptors';
import { isNumber } from '@shared/pipes/utils/utils';
import { CacheService } from '@shared/services';
import { ConfigService } from '@shared/services/core/config.service';

import { CompanyRepository } from './company.repository';

@Injectable({ providedIn: 'root' })
export class CompanyService {
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private readonly http: HttpClient,
		private readonly repo: CompanyRepository,
		private readonly env: ConfigService,
		private readonly cache: CacheService
	) {}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateSettings
	 **-------------------------------------------------------------------------------------
	 */
	public updateSettings({ companyId, settings }): Observable<any> {
		const url = `${this.env.get('companyEndPoint')}/${companyId}/settings`;
		return this.http.put(url, { settings }).pipe(retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getCompanyId
	 **-------------------------------------------------------------------------------------
	 */
	public getCompanyId() {
		return this.repo.getCompanyId();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - get
	 **-------------------------------------------------------------------------------------
	 */
	public get(): Observable<any> {
		const url = `${this.env.get('companyEndPoint')}/1`;
		return this.http
			.get(url, {
				context: withAPICache({
					ttl: CACHE_TIMEOUT,
				}),
			})
			.pipe(
				tap(resp => this.repo.add(resp)),
				retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY })
			);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - get
	 **-------------------------------------------------------------------------------------
	 */
	public getPublicCompany(companyId): Observable<any> {
		const url = `${this.env.get('companyEndPoint')}/public/${companyId}`;
		return this.http
			.get(url, {
				context: withAPICache({
					ttl: CACHE_TIMEOUT,
				}),
			})
			.pipe(
				tap(resp => this.repo.add(resp)),
				retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY })
			);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getCompanyAddOns
	 * *-------------------------------------------------------------------------------------
	 */
	public getCompanyAddOns(companyId): Observable<any> {
		const url = `${this.env.get('pricingEndPoint')}/pricing-booking/add-ons/company/${companyId}/global`;
		return this.http
			.get(url, {
				context: withAPICache({
					ttl: CACHE_TIMEOUT,
				}),
			})
			.pipe(
				tap(resp => this.repo.update({ id: companyId, key: 'add_ons', data: resp })),
				retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY })
			);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - listCompanies
	 **-------------------------------------------------------------------------------------
	 */
	public listCompanies(): Observable<any> {
		const url = `${this.env.get('companyEndPoint')}/mgmt/list`;
		return this.http.get(url, {});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getCompanyFeatures
	 **-------------------------------------------------------------------------------------
	 */
	public getCompanyFeatures(companyId) {
		if (!companyId) companyId = this.cache.get('secure:company_id');
		if (!isNumber(companyId)) return of({ featureAreas: [] });
		const url = `${this.env.get('featuresEndPoint')}/company/${companyId}`;
		return this.http
			.get(url, {
				context: withAPICache({
					ttl: CACHE_TIMEOUT,
				}),
			})
			.pipe(
				tap(resp => this.repo.update({ id: companyId, key: 'features', data: resp })),
				retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY })
			);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getFeatureActiveStatus
	 **-------------------------------------------------------------------------------------
	 */
	public async getFeatureActiveStatus(id, featureName: string) {
		const feature = await this.getFeature(id, featureName.trim());
		if (feature) {
			return feature.isActive;
		}

		return false;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getFeatureChildActiveStatus
	 **-------------------------------------------------------------------------------------
	 */
	public async getFeatureChildActiveStatus(id, featureName: string, childFeatureName: string, useLike: boolean = false): Promise<any> {
		const featureArea = await this.getFeature(id, featureName.trim());
		let feature = { isActive: false };
		if (!featureArea) return feature;
		if (useLike) {
			const search = new RegExp(childFeatureName, 'i');
			[feature] = featureArea.features.filter(e => search.test(e.name));
		} else {
			feature = featureArea.features.find(feat => feat.name.toLowerCase().trim() === childFeatureName.toLowerCase().trim());
		}
		if (feature) {
			return feature;
		}

		return { isActive: false };
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getFeature
	 **-------------------------------------------------------------------------------------
	 */
	public async getFeature(id, featureName: string) {
		const features: any = await this.getCompanyFeatures(id).toPromise();
		const feature = features.featureAreas.find(feat => feat.name.toLowerCase().trim() === featureName.toLowerCase().trim());
		return feature;
	}
}
